import { SLOTS_APIS } from "@/api_framework/api_config";
import { IBookingCancelationResponse, IBookingRescheduleBody } from "@/api_framework/api_modals/Bookings";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import axios, { AxiosError, AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useAPIUtils } from "../../useAPIUtils";
import { useConfiguration } from "../../useConfiguration";
import { useCurlLogger } from "../../useCurlLogger";
import { HOME_SERVICE_ID } from "@/constants";
export type RotaCreationReturnType = {
  status: boolean;
  message: string;
};

AxiosCurlirize(axios, () => {});


const parseCoordinate = (value?: number | null): number | null => {
  if (value === null || value === undefined) return null;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const isAtHomeServicePayload = (paramsBody: Partial<IBookingRescheduleBody>): boolean =>
  paramsBody?.service_details?.service_type_id === HOME_SERVICE_ID;

const shouldUseAtHomeRescheduleAPI = (paramsBody: Partial<IBookingRescheduleBody>): boolean => {
  const hasRequiredRescheduleFields = Boolean(
    paramsBody?.slot_details?.slot_id && paramsBody?.pet_details?.id && paramsBody?.user_details?.id,
  );
  const isCancelFlow = Boolean(paramsBody?.cancel_message);
  return isAtHomeServicePayload(paramsBody) && hasRequiredRescheduleFields && !isCancelFlow;
};

const buildAtHomeReschedulePayload = (paramsBody: Partial<IBookingRescheduleBody>, bookingId: string) => {
  return {
    booking_id: bookingId,
    slot_id: paramsBody?.slot_details?.slot_id,
    pet_id: paramsBody?.pet_details?.id,
    staff_id: paramsBody?.staff_details?.id ?? null,
    user_id: paramsBody?.user_details?.id,
    latitude: parseCoordinate(paramsBody?.latitude),
    longitude: parseCoordinate(paramsBody?.longitude),
    address: paramsBody?.address,
    address_id: paramsBody?.address_id,
    comments: paramsBody?.comments ?? "",
    reason: paramsBody?.reason ?? "",
    cart_details: paramsBody?.cart_details,
  };
};

const buildCancellationPayload = (paramsBody: Partial<IBookingRescheduleBody>, bookingId: string) => {
  return {
    booking_id: paramsBody?.booking_id ?? bookingId,
    reason: paramsBody?.reason ?? paramsBody?.cancel_message ?? "",
  };
};

export const useBookingRescheduleAndCancelAPI = () => {
  const configuration = useConfiguration();
  const [updatedBookingId, setUpdateBookingId] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const { sendCurl } = useCurlLogger();
  const { captureError } = useAPIUtils();
  const execute = useCallback(
    async (paramsBody: Partial<IBookingRescheduleBody>, bookingId: string) => {
      dispatch(
        setLoading({
          key: "isBookingCreationInProgress",
          status: true,
        }),
      );
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const isCancelFlow = Boolean(paramsBody?.cancel_message);
        const shouldUseAtHomeReschedule = shouldUseAtHomeRescheduleAPI(paramsBody);
        const endpoint = isCancelFlow
          ? SLOTS_APIS?.AT_HOME_BOOKING_CREATION?.baseURL
          : shouldUseAtHomeReschedule
            ? SLOTS_APIS?.AT_HOME_BOOKING_CREATION?.baseURL
            : `${SLOTS_APIS?.BOOKING_RECSHEDULE?.baseURL}/${bookingId}/`;
        const payload = isCancelFlow
          ? buildCancellationPayload(paramsBody, bookingId)
          : shouldUseAtHomeReschedule
            ? buildAtHomeReschedulePayload(paramsBody, bookingId)
            : paramsBody;
        const request = isCancelFlow
          ? axios.delete(endpoint ?? "", {
              data: payload,
              headers: {
                Authorization: `${accessToken}`,
                "Content-Type": "application/json",
              },
            })
          : axios.put(endpoint ?? "", payload, {
              headers: {
                Authorization: `${accessToken}`,
                "Content-Type": "application/json",
              },
            });
        const response: RotaCreationReturnType = await request
          .then((res: AxiosResponse<IBookingCancelationResponse>) => {
            if (res.data.status === true) {
              const resolvedBookingId =
                (res.data as any)?.data?.booking_id ?? (res.data as any)?.booking_id ?? bookingId ?? null;
              setUpdateBookingId(resolvedBookingId);

              dispatch(
                setLoading({
                  key: "isBookingCreationInProgress",
                  status: false,
                }),
              );
              toast.success("Booking updated successfully !");
              return { status: true, message: "" };
            } else {
              setUpdateBookingId(null);
              dispatch(
                setLoading({
                  key: "isBookingCreationInProgress",
                  status: false,
                }),
              );
            }
          })
          .catch((e: AxiosError) => {
            captureError(e);
            sendCurl(e.config["curlCommand"]);
            if (e.status === 400 || e.code === "ERR_BAD_REQUEST") {
              return { status: false, message: "" };
            }
          });
        return response;
      } catch (e: any) {
        toast.error("Server Error: " + e.message);
        return { status: false, message: "" };
      }
    },
    [dispatch, configuration, captureError, sendCurl],
  );
  return { updatedBookingId, execute };
};
